// What the team claims from jobs, and Setup's ceiling on it (finance-real, the user 2026-10-22): a claim added, changed
// (its person and amount, the user 2026-10-23) or removed. Under financeKeys.all, so a claim written refetches the job
// whose cap and margin it moves, its invoice, the team invoice it is on and /finance's lists (TD-102). Each read and
// write calls lineJobService, as useLineJobs does.
import { useQuery } from "@tanstack/react-query";
import type { ClaimSettings, JobClaimInput } from "../types";
import { financeKeys, useFinanceMutation } from "./financeQuery";
import { lineJobService } from "../services/lineJobService";

export const jobClaimKeys = {
  client: (clientId: string) => [...financeKeys.all, "claims", "client", clientId] as const,
  job: (jobId: string) => [...financeKeys.all, "claims", "job", jobId] as const,
  settings: () => [...financeKeys.all, "claim-settings"] as const,
};

export const useClientClaims = (clientId: string | undefined) =>
  useQuery({ queryKey: jobClaimKeys.client(clientId ?? "none"), queryFn: () => lineJobService.clientClaims(clientId ?? ""), enabled: !!clientId });

export const useJobClaims = (jobId: string) => useQuery({ queryKey: jobClaimKeys.job(jobId), queryFn: () => lineJobService.jobClaims(jobId) });

export const useClaimSettings = () => useQuery({ queryKey: jobClaimKeys.settings(), queryFn: lineJobService.claimSettings });

/** A refusal (more than is free) shows in the drawer, beside the amount: `refusedInline`. */
export const useAddClaim = () =>
  useFinanceMutation((v: { jobId: string; input: JobClaimInput }) => lineJobService.addClaim(v.jobId, v.input), (claim) => `${claim.name}'s claim is recorded.`, { refusedInline: true });

/** A refusal (more than is free, a claim on a received or paid team invoice) shows in the claim's form: `refusedInline`. */
export const useUpdateClaim = () =>
  useFinanceMutation((v: { claimId: string; input: JobClaimInput }) => lineJobService.updateClaim(v.claimId, v.input), (claim) => `${claim.name}'s claim is saved.`, { refusedInline: true });

/** A refusal (a claim on a received or paid team invoice) shows in the drawer, under the claims: `refusedInline`. */
export const useRemoveClaim = () =>
  useFinanceMutation((claimId: string) => lineJobService.removeClaim(claimId), (claim) => `${claim.name}'s claim is taken off.`, { refusedInline: true });

export const useSetClaimSettings = () =>
  useFinanceMutation((input: ClaimSettings) => lineJobService.setClaimSettings(input), (s) => `The team can claim at most ${s.maxClaimPct}% of a job.`);
